import React, { useLayoutEffect, useRef } from 'react';
import './ViveLibreSection.css';

const DURATION_DESKTOP_MS = 25000;
const DURATION_MOBILE_MS = 42000;

const DESKTOP_MEDIA = '(min-width: 769px)';

function readStripWidthPx(strip) {
  const a = strip.getBoundingClientRect().width;
  const b = strip.offsetWidth;
  const c = strip.scrollWidth;
  return Math.max(a || 0, b || 0, c || 0);
}

/**
 * Un solo motor RAF en todos los anchos (el mismo que ya funcionaba en móvil).
 * La animación CSS en escritorio era frágil con flex/max-content y keyframes (%, var()).
 */
const ViveLibreSection = () => {
  const repetitions = 14;
  const marqueeRef = useRef(null);
  const stripRef = useRef(null);
  const rafIdRef = useRef(0);

  useLayoutEffect(() => {
    const mqEl = marqueeRef.current;
    const strip = stripRef.current;
    if (!mqEl || !strip || typeof window === 'undefined') return undefined;

    const prefersReduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const mqDesktop = window.matchMedia(DESKTOP_MEDIA);

    const clearRaf = () => {
      if (rafIdRef.current) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
    };

    const cycleDurationMs = () => (mqDesktop.matches ? DURATION_DESKTOP_MS : DURATION_MOBILE_MS);

    const runRafMarquee = () => {
      clearRaf();

      let offsetPx = 0;
      let lastFrameTime = performance.now();

      const readCycleWidthPx = () => {
        const w = readStripWidthPx(strip);
        if (w > 100) return w;
        const track = mqEl.parentElement;
        if (track && track.clientWidth > 100) return Math.round(track.clientWidth * 0.95);
        return Math.max(readStripWidthPx(mqEl) / 2, 640);
      };

      const loop = (now) => {
        const dt = Math.min(now - lastFrameTime, 80);
        lastFrameTime = now;

        const cycleWidthPx = Math.max(readCycleWidthPx(), 320);
        const speedPxPerMs = cycleWidthPx / cycleDurationMs();

        offsetPx -= speedPxPerMs * dt;

        while (offsetPx <= -cycleWidthPx) {
          offsetPx += cycleWidthPx;
        }

        mqEl.style.transform = `translate3d(${offsetPx}px, 0, 0)`;
        rafIdRef.current = window.requestAnimationFrame(loop);
      };

      mqEl.style.transform = '';
      rafIdRef.current = window.requestAnimationFrame(loop);
    };

    const apply = () => {
      mqEl.removeAttribute('data-vive-marquee-engine');
      mqEl.style.transform = '';
      clearRaf();

      if (prefersReduced) {
        return;
      }

      mqEl.setAttribute('data-vive-marquee-engine', 'raf');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (prefersReduced) return;
          runRafMarquee();
        });
      });
    };

    apply();

    if (mqDesktop.addEventListener) {
      mqDesktop.addEventListener('change', apply);
    } else {
      mqDesktop.addListener(apply);
    }

    const onResize = () => {
      if (prefersReduced) return;
      if (mqEl.getAttribute('data-vive-marquee-engine') !== 'raf') return;
      runRafMarquee();
    };
    window.addEventListener('resize', onResize);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (prefersReduced) return;
        if (mqEl.getAttribute('data-vive-marquee-engine') !== 'raf') return;
        runRafMarquee();
      });
      resizeObserver.observe(strip);
    }

    return () => {
      mqEl.removeAttribute('data-vive-marquee-engine');
      mqEl.style.transform = '';
      clearRaf();
      window.removeEventListener('resize', onResize);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (mqDesktop.removeEventListener) {
        mqDesktop.removeEventListener('change', apply);
      } else {
        mqDesktop.removeListener(apply);
      }
    };
  }, []);

  const MushroomIcon = () => (
    <svg
      className="vive-libre-icon"
      viewBox="0 0 24 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 2C6 2 2 8 2 14C2 14 6 18 12 18C18 18 22 14 22 14C22 8 18 2 12 2Z"
        fill="currentColor"
      />
      <rect x="9" y="18" width="6" height="8" rx="1" fill="currentColor" opacity="0.85" />
      <circle cx="8" cy="10" r="1.2" fill="currentColor" opacity="0.6" />
      <circle cx="16" cy="9" r="1.2" fill="currentColor" opacity="0.6" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" opacity="0.6" />
    </svg>
  );

  const renderItem = (i, keyPrefix = '') => (
    <React.Fragment key={`${keyPrefix}${i}`}>
      <span className="vive-libre-phrase">{i % 2 === 0 ? 'VIVE LIBRE' : 'VIVE AUTÉNTICO'}</span>
      {i < repetitions - 1 && (
        <span className="vive-libre-separator" aria-hidden="true">
          <MushroomIcon />
        </span>
      )}
    </React.Fragment>
  );

  const renderStripItems = (keyPrefix = '') => (
    <>
      {Array.from({ length: repetitions }, (_, i) => renderItem(i, keyPrefix))}
      <span className="vive-libre-separator" aria-hidden="true">
        <MushroomIcon />
      </span>
    </>
  );

  return (
    <section className="vive-libre-section" aria-label="Vive libre, vive auténtico">
      <div className="vive-libre-track" aria-hidden="true">
        <div ref={marqueeRef} className="vive-libre-marquee">
          <div ref={stripRef} className="vive-libre-marquee-strip">
            {renderStripItems('a')}
          </div>
          <div className="vive-libre-marquee-strip">{renderStripItems('b')}</div>
        </div>
      </div>
    </section>
  );
};

export default ViveLibreSection;
